import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { useAuth } from '../auth';
import { useNotifications } from '../notifications';
import Logotext from '../components/logotext';
import { GET_HELP_CENTER, SUBMIT_HELP_CONTACT_FORM, GET_DEALS_FOR_CONTACT, GET_DOCUMENTS_FOR_CONTACT } from '../api/helpCenter';
import { HelpSection, HelpArticle } from '../gql/graphql';
import { ReactComponent as FolderIcon } from '../shared/icons/folder-icon.svg';
import { ReactComponent as ArticleIcon } from '../shared/icons/icon-template.svg';
import { ReactComponent as BackIcon } from '../shared/icons/chevron-left.svg';
import { ReactComponent as SearchIcon } from '../shared/icons/search-icon.svg';
import { paths } from '../routing';
import { get as getLatestTeamId } from '../utils/latest-team-id';
import styles from './help.module.scss';

interface BreadcrumbItem {
  id: string;
  title: string;
  slug: string;
}

type SearchResult = {
  type: 'section' | 'article';
  id: string;
  title: string;
  breadcrumb: string;
  slugPath: string;
  article?: HelpArticle;
  section?: HelpSection;
};

const STORAGE_KEY = 'help_contact_form';
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024; // 5MB

// Obfuscated email to deter scraper harvesting
const _e = () => ['su','pp','ort'].join('') + String.fromCharCode(64) + ['prop','docs','.com'].join('');

// Searchable dropdown component
interface SearchableSelectProps {
  items: Array<{ id: string; title: string }>;
  value: string;
  displayValue: string;
  onChange: (id: string, name: string) => void;
  placeholder: string;
  emptyMessage?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ items, value, displayValue, onChange, placeholder, emptyMessage = 'No results' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item => item.title.toLowerCase().includes(q));
  }, [items, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.searchableSelect} ref={containerRef}>
      <button
        type="button"
        className={classNames(styles.searchableSelectTrigger, isOpen && styles.searchableSelectOpen)}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <span className={value ? styles.searchableSelectValue : styles.searchableSelectPlaceholder}>
          {value ? displayValue : placeholder}
        </span>
        {value && (
          <span
            className={styles.searchableSelectClear}
            onClick={(e) => {
              e.stopPropagation();
              onChange('', '');
              setIsOpen(false);
            }}
          >
            &times;
          </span>
        )}
        <span className={styles.searchableSelectArrow}>&#9662;</span>
      </button>
      {isOpen && (
        <div className={styles.searchableSelectDropdown}>
          <div className={styles.searchableSelectSearchWrap}>
            <SearchIcon className={styles.searchableSelectSearchIcon} />
            <input
              ref={inputRef}
              type="text"
              className={styles.searchableSelectSearch}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.searchableSelectList}>
            {filtered.length === 0 ? (
              <div className={styles.searchableSelectEmpty}>{emptyMessage}</div>
            ) : (
              filtered.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={classNames(styles.searchableSelectItem, item.id === value && styles.searchableSelectItemActive)}
                  onClick={() => {
                    onChange(item.id, item.title);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  {item.title}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Find section/article by slug path
export function findBySlugPath(sections: HelpSection[], sectionSlug?: string, articleSlug?: string): { section?: HelpSection; article?: HelpArticle; breadcrumbs: BreadcrumbItem[] } {
  if (!sectionSlug) return { breadcrumbs: [] };

  const search = (items: HelpSection[], path: BreadcrumbItem[]): { section?: HelpSection; article?: HelpArticle; breadcrumbs: BreadcrumbItem[] } => {
    for (const section of items) {
      if (section.slug === sectionSlug) {
        const crumbs = [...path, { id: section.id, title: section.title, slug: section.slug }];
        if (articleSlug) {
          const article = (section.articles || []).find(a => a.slug === articleSlug);
          if (article) return { section, article, breadcrumbs: crumbs };
        }
        return { section, breadcrumbs: crumbs };
      }
      if (section.children?.length) {
        const found = search(section.children as HelpSection[], [...path, { id: section.id, title: section.title, slug: section.slug }]);
        if (found.section) return found;
      }
    }
    return { breadcrumbs: [] };
  };

  return search(sections, []);
}

const HelpCenterPage: React.FC = () => {
  const [{ user, loggedIn }] = useAuth();
  const [, { error: notifyError }] = useNotifications();
  const navigate = useNavigate();
  const { sectionSlug, articleSlug } = useParams<{ sectionSlug?: string; articleSlug?: string }>();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubject, setContactSubject] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}').subject || ''; } catch { return ''; }
  });
  const [contactMessage, setContactMessage] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}').message || ''; } catch { return ''; }
  });
  const [selectedDealId, setSelectedDealId] = useState('');
  const [selectedDealName, setSelectedDealName] = useState('');
  const [selectedDocId, setSelectedDocId] = useState('');
  const [selectedDocName, setSelectedDocName] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persist form text fields
  useEffect(() => {
    if (contactSubject || contactMessage) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ subject: contactSubject, message: contactMessage }));
    }
  }, [contactSubject, contactMessage]);

  const clearFormState = useCallback(() => {
    setContactSubject('');
    setContactMessage('');
    setSelectedDealId('');
    setSelectedDealName('');
    setSelectedDocId('');
    setSelectedDocName('');
    setAttachmentFile(null);
    setAttachmentPreview(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Data
  const { data, loading } = useQuery(GET_HELP_CENTER, { fetchPolicy: 'cache-and-network' });
  const [submitContactForm, { loading: submitting }] = useMutation(SUBMIT_HELP_CONTACT_FORM);

  // Deals for contact form (only for logged-in users with team context)
  const teamId = user?.id ? getLatestTeamId(user.id) : null;

  const { data: dealsData } = useQuery(GET_DEALS_FOR_CONTACT, {
    variables: { teamId: teamId || '' },
    skip: !teamId || !showContactForm || !loggedIn,
  });
  const deals: Array<{ id: string; title: string }> = dealsData?.dealsForTeam || [];

  const [fetchDocuments, { data: docsData }] = useLazyQuery(GET_DOCUMENTS_FOR_CONTACT);
  const documents: Array<{ id: string; title: string }> = docsData?.getDocumentsByDeal || [];

  // Auto-open contact form when redirected back after login
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('contact') === '1') {
      setShowContactForm(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedDealId) {
      fetchDocuments({ variables: { dealId: selectedDealId } });
    }
  }, [selectedDealId, fetchDocuments]);

  const sections: HelpSection[] = data?.helpCenter ?? [];

  // Resolve URL slugs to current section/article
  const resolved = useMemo(() => findBySlugPath(sections, sectionSlug, articleSlug), [sections, sectionSlug, articleSlug]);
  const currentSection = resolved.section || null;
  const selectedArticle = resolved.article || null;
  const breadcrumbs = resolved.breadcrumbs;

  // Flatten for search
  const { allArticles, allSections } = useMemo(() => {
    const articles: Array<HelpArticle & { breadcrumb: string; slugPath: string }> = [];
    const sectionList: Array<HelpSection & { breadcrumb: string; slugPath: string }> = [];

    const traverse = (items: HelpSection[], path: string[] = []) => {
      for (const section of items) {
        const currentPath = [...path, section.title];
        (sectionList as any[]).push({ ...section, breadcrumb: currentPath.join(' > '), slugPath: section.slug });
        for (const article of (section.articles || [])) {
          (articles as any[]).push({ ...article, breadcrumb: currentPath.join(' > '), slugPath: `${section.slug}/${article.slug}` });
        }
        if (section.children?.length) {
          traverse(section.children as HelpSection[], currentPath);
        }
      }
    };
    traverse(sections);
    return { allArticles: articles, allSections: sectionList };
  }, [sections]);

  // Search results (title only)
  const searchResults = useMemo<SearchResult[]>(() => {
    if (!searchQuery.trim()) return [];
    const words = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const results: SearchResult[] = [];

    for (const section of allSections) {
      const title = section.title.toLowerCase();
      if (words.every(w => title.includes(w))) {
        results.push({ type: 'section', id: section.id, title: section.title, breadcrumb: (section as any).breadcrumb, slugPath: (section as any).slugPath, section });
      }
    }
    for (const article of allArticles) {
      const title = article.title.toLowerCase();
      if (words.some(w => title.includes(w))) {
        results.push({ type: 'article', id: article.id, title: article.title, breadcrumb: (article as any).breadcrumb, slugPath: (article as any).slugPath, article });
      }
    }
    return results.slice(0, 20);
  }, [searchQuery, allArticles, allSections]);

  // Navigation
  const navigateToSection = useCallback((section: HelpSection) => {
    navigate(`/help/${section.slug}`);
    setSearchQuery('');
  }, [navigate]);

  const navigateToArticle = useCallback((article: HelpArticle, section: HelpSection) => {
    navigate(`/help/${section.slug}/${article.slug}`);
    setSearchQuery('');
  }, [navigate]);

  // File attachment handlers
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) {
      notifyError({ message: 'File too large. Maximum size is 5MB.' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      notifyError({ message: 'Only image files are supported.' });
      return;
    }
    setAttachmentFile(file);
    const reader = new FileReader();
    reader.onload = () => setAttachmentPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, [notifyError]);

  const removeAttachment = useCallback(() => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Contact form submit
  const handleContactSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactSubject.trim() || !contactMessage.trim()) return;

    try {
      const variables: Record<string, any> = {
        subject: contactSubject,
        message: contactMessage,
      };
      if (selectedDealId) {
        variables.dealName = selectedDealName;
        variables.dealId = selectedDealId;
      }
      if (selectedDocId) {
        variables.documentName = selectedDocName;
        variables.documentId = selectedDocId;
      }
      if (attachmentFile && attachmentPreview) {
        variables.attachmentData = attachmentPreview;
        variables.attachmentFilename = attachmentFile.name;
      }

      await submitContactForm({ variables });
      setShowConfirmation(true);
      clearFormState();
      setTimeout(() => {
        setShowContactForm(false);
        setShowConfirmation(false);
      }, 3000);
    } catch {
      notifyError({ message: 'Failed to send message. Please try again.' });
    }
  }, [contactSubject, contactMessage, selectedDealId, selectedDealName, selectedDocId, selectedDocName, attachmentFile, attachmentPreview, submitContactForm, notifyError, clearFormState]);

  // Current items to display
  const currentItems = useMemo(() => {
    const source = currentSection && !selectedArticle
      ? { children: (currentSection.children || []) as HelpSection[], articles: currentSection.articles || [] }
      : { children: sections, articles: [] as HelpArticle[] };

    return {
      sections: [...source.children].sort((a, b) => a.title.localeCompare(b.title)),
      articles: [...source.articles].sort((a, b) => a.title.localeCompare(b.title)),
    };
  }, [currentSection, selectedArticle, sections]);

  // SEO metadata
  const pageTitle = selectedArticle
    ? `${selectedArticle.title} - Help Center | Propdocs`
    : currentSection
      ? `${currentSection.title} - Help Center | Propdocs`
      : 'Help Center | Propdocs';

  const pageDescription = selectedArticle
    ? `${selectedArticle.title} — ${currentSection?.title ? `${currentSection.title} — ` : ''}Propdocs Help Center`
    : currentSection
      ? currentSection.description || `Browse ${currentSection.title} articles in the Propdocs Help Center.`
      : 'Find answers, browse help articles, and get support for Propdocs — the modern platform for commercial real estate transactions.';

  const canonicalUrl = selectedArticle
    ? `https://app.propdocs.com/help/${sectionSlug}/${articleSlug}`
    : currentSection
      ? `https://app.propdocs.com/help/${sectionSlug}`
      : 'https://app.propdocs.com/help';

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Propdocs Help Center" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://app.propdocs.com/images/propdocs-og.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://app.propdocs.com/images/propdocs-og.png" />
      </Helmet>

      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logoLink}>
            <Logotext isFull />
          </Link>
          <nav className={styles.headerNav}>
            {loggedIn && (
              <Link to={paths.home} className={classNames(styles.headerNavLink, styles.backToApp)}>Back to App</Link>
            )}
            <button className={styles.contactButton} onClick={() => setShowContactForm(true)}>
              <svg className={styles.contactButtonIcon} viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
              <span className={styles.contactButtonLabel}>Contact Us</span>
            </button>
            {loggedIn ? (
              <div className={styles.userPill}>
                <div className={styles.userAvatar}>{user?.fullName?.charAt(0)?.toUpperCase() || '?'}</div>
                <span className={styles.userName}>{user?.fullName}</span>
              </div>
            ) : (
              <Link to={paths.auth.login} className={styles.headerNavLink}>Sign In</Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      {!selectedArticle && !showContactForm && (
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <h1 className={styles.heroTitle}>How can we help?</h1>
            <div className={styles.heroSearchContainer}>
              <SearchIcon className={styles.heroSearchIcon} />
              <input
                type="text"
                placeholder="Search for articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.heroSearchInput}
              />
              {searchQuery && (
                <button className={styles.heroClearSearch} onClick={() => setSearchQuery('')}>&times;</button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Breadcrumbs (sticky below header) */}
      {!showContactForm && !selectedArticle && !searchQuery.trim() && breadcrumbs.length > 0 && (
        <div className={styles.breadcrumbBar}>
          <div className={styles.breadcrumbBarInner}>
            <Link to="/help" className={styles.breadcrumbLink}>Help Center</Link>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id}>
                <span className={styles.breadcrumbSeparator}>/</span>
                {index === breadcrumbs.length - 1 ? (
                  <span className={styles.breadcrumbCurrent}>{crumb.title}</span>
                ) : (
                  <Link to={`/help/${crumb.slug}`} className={styles.breadcrumbLink}>{crumb.title}</Link>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={styles.main}>
        <div className={styles.mainInner}>

          {/* Contact Form */}
          {showContactForm ? (
            <div className={styles.contactCard}>
              <button className={styles.backLink} onClick={() => setShowContactForm(false)}>
                <BackIcon /> Back to Help Center
              </button>

              {loggedIn && showConfirmation ? (
                <div className={styles.confirmation}>
                  <div className={styles.confirmationIcon}>&#10003;</div>
                  <h3>Message Sent</h3>
                  <p>We'll be in touch shortly.</p>
                </div>
              ) : (
                <>
              <h2 className={styles.contactTitle}>Contact Us</h2>
              <p className={styles.contactSubtitle}>
                {loggedIn
                  ? "Describe your issue and we'll get back to you as soon as possible."
                  : "Send us an email and we'll get back to you shortly."
                }
              </p>

              {loggedIn ? (
                  <form onSubmit={handleContactSubmit} className={styles.contactForm}>
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label>Name</label>
                        <input type="text" value={user?.fullName || ''} disabled className={styles.formInput} />
                      </div>
                      <div className={styles.formGroup}>
                        <label>Email</label>
                        <input type="email" value={user?.email || ''} disabled className={styles.formInput} />
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label>Subject</label>
                      <input
                        type="text"
                        value={contactSubject}
                        onChange={(e) => setContactSubject(e.target.value)}
                        placeholder="What do you need help with?"
                        className={styles.formInput}
                        required
                      />
                    </div>

                    {teamId && (
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>Deal <span className={styles.optionalLabel}>(optional)</span></label>
                          <SearchableSelect
                            items={deals}
                            value={selectedDealId}
                            displayValue={selectedDealName}
                            placeholder="Select a deal..."
                            emptyMessage="No deals found"
                            onChange={(id, name) => {
                              setSelectedDealId(id);
                              setSelectedDealName(name);
                              setSelectedDocId('');
                              setSelectedDocName('');
                            }}
                          />
                        </div>
                        {selectedDealId && (
                          <div className={styles.formGroup}>
                            <label>Document <span className={styles.optionalLabel}>(optional)</span></label>
                            <SearchableSelect
                              items={documents}
                              value={selectedDocId}
                              displayValue={selectedDocName}
                              placeholder="Select a document..."
                              emptyMessage="No documents"
                              onChange={(id, name) => {
                                setSelectedDocId(id);
                                setSelectedDocName(name);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.formGroup}>
                      <label>Message</label>
                      <textarea
                        value={contactMessage}
                        onChange={(e) => setContactMessage(e.target.value)}
                        placeholder="Describe your issue or question..."
                        className={styles.formTextarea}
                        rows={6}
                        required
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label>Screenshot <span className={styles.optionalLabel}>(optional, max 5MB)</span></label>
                      <div className={styles.attachmentArea}>
                        {attachmentPreview ? (
                          <div className={styles.attachmentPreview}>
                            <img src={attachmentPreview} alt="Attachment preview" />
                            <button type="button" className={styles.removeAttachment} onClick={removeAttachment}>&times;</button>
                            <span className={styles.attachmentName}>{attachmentFile?.name}</span>
                          </div>
                        ) : (
                          <button type="button" className={styles.attachButton} onClick={() => fileInputRef.current?.click()}>
                            + Attach Image
                          </button>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>

                    <button type="submit" className={styles.submitButton} disabled={submitting || !contactSubject.trim() || !contactMessage.trim()}>
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
              ) : (
                <div className={styles.guestContact}>
                  <button type="button" className={styles.mailtoButton} onClick={() => { window.location.href = 'mai' + 'lto:' + _e(); }}>
                    Email Us
                  </button>
                  <div className={styles.guestDivider} />
                  <p className={styles.guestSignInNote}>Have an account? Sign in to get faster support.</p>
                  <Link to={paths.auth.login} state={{ returnTo: '/help?contact=1' }} className={styles.guestSignInButton}>Sign In</Link>
                </div>
              )}
                </>
              )}
            </div>
          ) : selectedArticle ? (
            /* Article View */
            <div className={styles.articleCard}>
              <div className={styles.articleBreadcrumbs}>
                <Link to="/help" className={styles.breadcrumbLink}>Help Center</Link>
                {breadcrumbs.map((crumb) => (
                  <React.Fragment key={crumb.id}>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <Link to={`/help/${crumb.slug}`} className={styles.breadcrumbLink}>{crumb.title}</Link>
                  </React.Fragment>
                ))}
              </div>

              <h1 className={styles.articleTitle}>{selectedArticle.title}</h1>
              <div className={styles.articleMeta}>
                Last updated: {new Date(selectedArticle.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div
                className={styles.articleBody}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.body) }}
              />
              <div className={styles.articleFooter}>
                <div className={styles.articleFooterDivider} />
                <p className={styles.articleFooterText}>Still need help?</p>
                <button className={styles.contactCtaButton} onClick={() => setShowContactForm(true)}>
                  Contact Us
                </button>
              </div>
            </div>
          ) : (
            /* Browse View */
            <>
              {/* Search Results */}
              {searchQuery.trim() ? (
                <div className={styles.searchResults}>
                  {searchResults.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>No results found for &ldquo;{searchQuery}&rdquo;</p>
                      <button className={styles.contactLinkButton} onClick={() => { setSearchQuery(''); setShowContactForm(true); }}>
                        Contact us for help
                      </button>
                    </div>
                  ) : (
                    searchResults.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        className={styles.searchResultItem}
                        onClick={() => navigate(`/help/${result.slugPath}`)}
                      >
                        <div className={styles.searchResultIcon}>
                          {result.type === 'section' ? <FolderIcon /> : <ArticleIcon />}
                        </div>
                        <div className={styles.searchResultContent}>
                          <div className={styles.searchResultTitle}>{result.title}</div>
                          <div className={styles.searchResultBreadcrumb}>{result.breadcrumb}</div>
                        </div>
                        <span className={classNames(styles.searchResultBadge, result.type === 'section' ? styles.badgeFolder : styles.badgeArticle)}>
                          {result.type === 'section' ? 'Folder' : 'Article'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ) : (
                <>
                  {/* Loading */}
                  {loading && !data && (
                    <div className={styles.emptyState}>Loading...</div>
                  )}

                  {/* Section Grid */}
                  {currentItems.sections.length > 0 && (
                    <div className={classNames(styles.grid, currentSection && styles.gridList)}>
                      {currentItems.sections.map((section) => (
                        <button
                          key={section.id}
                          className={classNames(styles.sectionCard, currentSection && styles.sectionCardCompact)}
                          onClick={() => navigateToSection(section)}
                        >
                          <FolderIcon className={styles.sectionIcon} />
                          <div className={styles.sectionCardContent}>
                            <div className={styles.sectionCardTitle}>{section.title}</div>
                            {!currentSection && section.description && (
                              <div className={styles.sectionCardDescription}>{section.description}</div>
                            )}
                          </div>
                          <span className={styles.chevron}>&rsaquo;</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Article List */}
                  {currentItems.articles.length > 0 && (
                    <div className={styles.articleList}>
                      {currentItems.articles.map((article) => (
                        <button
                          key={article.id}
                          className={styles.articleListItem}
                          onClick={() => currentSection && navigateToArticle(article, currentSection)}
                        >
                          <ArticleIcon className={styles.articleListIcon} />
                          <span className={styles.articleListTitle}>{article.title}</span>
                          <span className={styles.chevron}>&rsaquo;</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Empty state */}
                  {!loading && currentItems.sections.length === 0 && currentItems.articles.length === 0 && (
                    <div className={styles.emptyState}>
                      {currentSection ? 'This section is empty.' : 'No help articles available yet.'}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <Logotext isStacked />
          <span className={styles.footerCopy}>&copy;2026 Propdocs Technologies Inc.</span>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
